import type { ReactNode } from 'react';
import { Button } from '../atoms/Button';
import { Icon } from '../atoms/Icon';
import type { IconName } from '../icons/registry';
import styles from './Alert.module.css';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

export interface AlertProps {
  tone?: AlertTone;
  title?: string;
  children?: ReactNode;
  /** Actions shown below the text, e.g. a button. */
  actions?: ReactNode;
  /** Shows a close button; requires `dismissLabel`. */
  onDismiss?: () => void;
  dismissLabel?: string;
}

const icons: Record<AlertTone, IconName> = {
  info: 'info',
  success: 'check-circle',
  warning: 'alert-triangle',
  danger: 'alert-circle',
};

/** Inline message for lasting states. Danger alerts are announced immediately. */
export function Alert({
  tone = 'info',
  title,
  children,
  actions,
  onDismiss,
  dismissLabel,
}: AlertProps) {
  return (
    <div
      className={`${styles.alert} ${styles[tone]}`}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      <Icon name={icons[tone]} size="sm" className={styles.icon} />
      <div className={styles.body}>
        {title && <strong className={styles.title}>{title}</strong>}
        {children && <div>{children}</div>}
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>
      {onDismiss && dismissLabel && (
        <Button
          variant="ghost"
          size="xs"
          aria-label={dismissLabel}
          onClick={onDismiss}
          iconStart={<Icon name="close" size="sm" />}
        />
      )}
    </div>
  );
}
